import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FiClock } from 'react-icons/fi';
import './TimeField.css';

/**
 * A time control that reads as UNSET when it is unset, and opens OUR OWN picker
 * on tap.
 *
 * A bare `<input type="time">` failed on both counts. Empty it renders "--:--",
 * and the moment it takes focus a stray scroll or keypress fills a
 * plausible-looking time — so an organiser could not tell a tee time they chose
 * from one the browser invented. It also only opens its picker from the small
 * clock glyph at the end; the rest of the field expects typing.
 *
 * The first fix kept the native input and drove it with `showPicker()`. That is
 * not dependable here: `showPicker()` is absent or a no-op for time inputs
 * across the four engines this app ships into (desktop Chrome, desktop Safari,
 * iOS WKWebView and the Android System WebView, both via Capacitor), so the
 * field read as dead on tap. This picker is plain DOM and behaves identically
 * in all of them.
 *
 * Minutes step by 5 — tee times are not set to the minute — but a value already
 * stored off-step (an older sheet, or one edited by hand) is inserted into the
 * list rather than silently rounded away.
 */

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTE_STEP = 5;
const pad = (n) => String(n).padStart(2, '0');

// The draft a picker opens on when nothing is set yet. It is only ever a
// STARTING POSITION for the wheels — the field stays empty until "Set time" is
// pressed, so this can never masquerade as a chosen tee time.
const DEFAULT_DRAFT = { hour: 8, minute: 0 };

const parse = (value) => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value || '');
    if (!match) return null;
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour > 23 || minute > 59) return null;
    return { hour, minute };
};

const TimeField = ({
    value,
    onChange,
    disabled,
    name,
    id,
    ariaLabel = 'Time',
    placeholder = 'Select time',
    title = 'Select a time',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [draft, setDraft] = useState(DEFAULT_DRAFT);
    const hourColRef = useRef(null);
    const minuteColRef = useRef(null);

    const parsed = useMemo(() => parse(value), [value]);

    // Whole steps, plus whatever the stored value actually is if it sits
    // between them — rounding someone's 08:37 to 08:35 behind their back is
    // exactly the kind of quiet edit this component exists to stop.
    const minutes = useMemo(() => {
        const list = [];
        for (let m = 0; m < 60; m += MINUTE_STEP) list.push(m);
        if (parsed && !list.includes(parsed.minute)) {
            list.push(parsed.minute);
            list.sort((a, b) => a - b);
        }
        return list;
    }, [parsed]);

    const open = () => {
        if (disabled) return;
        setDraft(parsed || DEFAULT_DRAFT);
        setIsOpen(true);
    };

    // Bring both selections into view on open. Without this the picker opens
    // showing midnight and the organiser has to scroll to find where they are.
    // Optional-called: `scrollIntoView` is missing in jsdom and in older
    // WebViews, and a picker that throws on open is far worse than one that
    // opens scrolled to the top.
    useEffect(() => {
        if (!isOpen) return;
        [hourColRef, minuteColRef].forEach((ref) => {
            const selected = ref.current?.querySelector('[data-selected="true"]');
            selected?.scrollIntoView?.({ block: 'center' });
        });
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen]);

    const commit = () => {
        const next = `${pad(draft.hour)}:${pad(draft.minute)}`;
        // Shaped like a change event so callers that read `e.target.name` (the
        // wizard's shared `handleChange`) keep working unchanged.
        onChange({ target: { name, value: next, type: 'time' } });
        setIsOpen(false);
    };

    return (
        <>
            <button
                type="button"
                id={id}
                className={
                    `timefield${value ? '' : ' timefield--empty'}${disabled ? ' timefield--disabled' : ''}`
                }
                onClick={open}
                disabled={disabled}
                aria-label={value ? `${ariaLabel}: ${value}` : ariaLabel}
                aria-haspopup="dialog"
            >
                <FiClock className="timefield__icon" aria-hidden="true" />
                <span className="timefield__value">{value || placeholder}</span>
            </button>

            {isOpen && (
                <div className="timepicker-overlay" onClick={() => setIsOpen(false)}>
                    <div
                        className="timepicker"
                        role="dialog"
                        aria-modal="true"
                        aria-label={title}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="timepicker__head">
                            <span className="timepicker__title">{title}</span>
                            <span className="timepicker__preview">
                                {pad(draft.hour)}:{pad(draft.minute)}
                            </span>
                        </div>

                        <div className="timepicker__cols">
                            <div className="timepicker__col">
                                <span className="timepicker__collabel">Hour</span>
                                <div className="timepicker__list" ref={hourColRef}>
                                    {HOURS.map((h) => (
                                        <button
                                            key={h}
                                            type="button"
                                            data-selected={h === draft.hour}
                                            className={`timepicker__opt${h === draft.hour ? ' timepicker__opt--on' : ''}`}
                                            onClick={() => setDraft((d) => ({ ...d, hour: h }))}
                                        >
                                            {pad(h)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="timepicker__col">
                                <span className="timepicker__collabel">Minute</span>
                                <div className="timepicker__list" ref={minuteColRef}>
                                    {minutes.map((m) => (
                                        <button
                                            key={m}
                                            type="button"
                                            data-selected={m === draft.minute}
                                            className={`timepicker__opt${m === draft.minute ? ' timepicker__opt--on' : ''}`}
                                            onClick={() => setDraft((d) => ({ ...d, minute: m }))}
                                        >
                                            {pad(m)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="timepicker__actions">
                            <button type="button" className="timepicker__cancel" onClick={() => setIsOpen(false)}>
                                Cancel
                            </button>
                            <button type="button" className="timepicker__set" onClick={commit}>
                                Set time
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default TimeField;
